import React, { useState, useEffect } from 'react';
import { CardHeader, CardTitle, CardContent } from '../../components/common/ui/card';
import { AlertCircle, Coins, Search } from 'lucide-react';
import TokenAllocationForm from '../TokenAllocationForm';
import DeleteTokenForm from './DeleteToken';
import api from '../../services/api';
import { Card } from '../../components/common/ui/card';

const TokenManagement = () => {
  const userRole = JSON.parse(localStorage.getItem('user') || '{}').role;
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [formMode, setFormMode] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search query
    const filtered = users.filter((user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users/tokens');
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAllocationSuccess = async () => {
    await fetchUsers();
    setSelectedUser(null);
    setFormMode('');
  };

  const isExpired = (date) => {
    return new Date(date) < new Date();
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    console.log('Selected user:', selectedUser);
  }, [selectedUser]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center text-gray-900">
              <Coins className="h-5 w-5 mr-2 text-orange-600" />
              Token Management
            </CardTitle>
            {userRole === 'admin' && (
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-2 bg-red-100 text-red-600 rounded flex items-center">
              <AlertCircle className="w-4 h-4 mr-2" />
              {error}
            </div>
          )}
          <div className="grid gap-6">
            {filteredUsers.map((user) => (
              <Card key={user._id} className="bg-white py-5">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold">
                      {user.name[0].toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{user.name}</h3>
                      <p className="text-sm text-gray-600">{user.email}</p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Tokens:</span>
                      <span className="font-medium text-gray-900">{user.tokens?.total || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Remaining:</span>
                      <span className="font-medium text-orange-600">{user.tokens?.remaining || 0}</span>
                    </div>
                    {user.tokens?.expiryDate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Expires:</span>
                        <span className={
                          isExpired(user.tokens.expiryDate)
                            ? 'text-red-600'
                            : 'text-green-600'
                        }>
                          {new Date(user.tokens.expiryDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {user.tokens?.history && user.tokens.history.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Recent Activity</h4>
                      <div className={`${userRole === 'admin' ? 'max-h-40' : 'max-h-[60vh]'} overflow-y-auto`}>
                        {user.tokens.history.map((entry, index) => (
                          <div
                            key={index}
                            className="p-2 bg-orange-50 rounded text-sm"
                          >
                            <div className="flex justify-between">
                              <span className={
                                entry.type === 'credit'
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }>
                                {entry.type === 'credit' ? '+' : '-'}{entry.amount}
                              </span>
                              <span className="text-gray-500">
                                {new Date(entry.timestamp).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-gray-600 mt-1">
                              {entry.operation}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {userRole === 'admin' && (
                    <div className="mt-4">
                      <button
                        onClick={() => {
                          setFormMode('ALLOCATE');
                          setSelectedUser(user);
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                      >
                        Allocate Tokens
                      </button>
                      <button
                        onClick={() => {
                          setFormMode('DELETE');
                          setSelectedUser(user);
                        }}
                        className="w-full mt-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                      >
                        Delete Tokens
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {selectedUser && formMode == "ALLOCATE" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium mb-4 text-gray-900">
              Allocate Tokens to {selectedUser.name}
            </h3>
            <TokenAllocationForm
              user={selectedUser}
              onSuccess={handleAllocationSuccess}
              onCancel={() => setSelectedUser(null)}
            />
          </div>
        </div>
      )}

      {selectedUser && formMode == "DELETE" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium mb-4 text-gray-900">
              Delete Tokens for {selectedUser.name}
            </h3>
            <DeleteTokenForm
              user={selectedUser}
              onSuccess={handleAllocationSuccess}
              onCancel={() => setSelectedUser(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenManagement;